import type { ActorEvent, ButtonItem, RichTextContentProtocol } from '@lighthouse/core'
import {
    type DataSourceAbstract,
    DataSourceType,
    type FilterCommonCondition,
    type GroupConfigure,
    type QuickFilterData,
    type Sorter,
    type TableColumns,
    type ViewField,
    type ViewType
} from '@lighthouse/core'
import { find } from 'rambda'
import React, { useMemo, useRef } from 'react'

import { useApplicationContext } from '../../contexts'
import type { ViewAppendParams } from '../../types'
import { getIsIncludesDisableDataSourceInWorkFlow } from '../../utils'
import { BlockTitle } from '../BlockTitle'
import { QuickFilterDrawer } from '../QuickFilterDrawer'
import { DebounceSearch } from '../Search'
import { Display, Group, Import, Sort } from '../TableSetting'
import { KanbanGroup } from '../TableSetting/KanbanGroup'
import { TailActionButtonGroup } from '../TailActionButtonGroup'
import { FilterActor } from '../ViewFieldFilterConfigure'
import * as SC from './styles'

type ViewSettingToolbarStyles = 'root'

interface ViewSettingToolbarProps {
    appId: string
    envId: string
    dsId: string
    uniqueId: string
    viewType: ViewType
    showTitle?: boolean
    title?: string
    search?: string
    dataSource: DataSourceAbstract
    dataSourceList: DataSourceAbstract[]
    userImportSetting?: string[]
    userSortSetting?: string[]
    kanbanGroupConfig?: GroupConfigure
    onKanbanGroupConfigChange?: (v: GroupConfigure) => void
    quickFilter?: QuickFilterData
    settingColumns: ViewField[]
    columns: ViewField[]
    sort?: Sorter[]
    group?: GroupConfigure
    enableGroup?: boolean
    enableImport?: boolean
    enableSort?: boolean
    enableDisplay?: boolean
    enableSetKanban?: boolean
    enableSearch?: boolean
    styles?: Partial<Record<ViewSettingToolbarStyles, React.CSSProperties>>
    toolbar?: ButtonItem[]
    onFilter?: (data: FilterCommonCondition[]) => void
    onSearch?: (value: string) => void
    onChangeSorter?: (data?: Sorter[]) => void
    onChangeDisplay?: (data?: TableColumns) => void
    onChangeGroup?: (data: GroupConfigure) => void
    onAppend?: (params: ViewAppendParams) => Promise<boolean>
    onActionTrigger?: (id: string, params: ActorEvent) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol) => string
}

export const ViewSettingToolbar: React.FC<ViewSettingToolbarProps> = ({
    appId,
    envId,
    dsId,
    uniqueId,
    viewType,
    showTitle,
    title,
    search,
    dataSource,
    dataSourceList,
    userImportSetting,
    userSortSetting,
    quickFilter,
    settingColumns,
    columns,
    sort,
    group,
    enableGroup,
    kanbanGroupConfig,
    enableImport,
    enableSort,
    enableDisplay,
    enableSetKanban,
    enableSearch = false,
    styles,
    toolbar,
    onKanbanGroupConfigChange,
    onFilter,
    onSearch,
    onChangeSorter,
    onChangeDisplay,
    onChangeGroup,
    onAppend,
    onActionTrigger,
    onRenderButtonTitle
}) => {
    // const [printModalOpen, setPrintModalOpen] = useToggle(false)
    const actionRef = useRef<HTMLDivElement>(null)
    const { schema } = dataSource
    const quickFilterRules = quickFilter?.rules
    const isExistedSetting = (quickFilterRules && quickFilterRules?.length > 0) || enableSearch
    const isNormalQuickFilter = quickFilter?.mode === 'normal'
    const isTileQuickFilter = quickFilter?.mode === 'tile'
    const noData = !dsId

    const isUserDataSource = useMemo(() => getIsIncludesDisableDataSourceInWorkFlow(dataSource), [dataSource])
    const firstTableProps = useMemo(() => {
        return find(item => item.visible, columns || [])?.fieldId
    }, [columns])

    const { isMobile } = useApplicationContext()

    const mobileContentStyles: React.CSSProperties = useMemo(() => {
        return isMobile ? { width: '100%' } : {}
    }, [isMobile])
    const action = useMemo(() => {
        if (isUserDataSource) {
            return null
        }

        if (toolbar?.length) {
            return (
                <TailActionButtonGroup
                    uniqueId={uniqueId}
                    style={{ marginLeft: 8 }}
                    group={toolbar}
                    onActionTrigger={onActionTrigger}
                    onRenderTitle={onRenderButtonTitle}
                />
            )
        }
    }, [isUserDataSource, onActionTrigger, onRenderButtonTitle, toolbar, uniqueId])

    const settingContent = useMemo(() => {
        return (
            <SC.ViewSettingToolbarContent>
                <SC.ViewActionPositive isMobile={isMobile}>
                    {enableSearch && !isMobile && <DebounceSearch isTheme value={search} isFull={isMobile} onSearch={onSearch} />}
                    {!isMobile && quickFilterRules && quickFilterRules?.length > 0 && quickFilter?.mode === 'normal' && (
                        <FilterActor
                            mode={quickFilter?.mode}
                            columns={settingColumns}
                            dataSource={dataSource}
                            isMobile={isMobile}
                            quickFilter={quickFilter}
                            onFilter={onFilter}
                        />
                    )}
                </SC.ViewActionPositive>

                <SC.ViewActionContainer ref={actionRef} style={mobileContentStyles}>
                    {isMobile && showTitle && viewType !== 'custom' && <BlockTitle fontSize={20} title={title} />}
                    <SC.ViewSettingContent>
                        {isMobile && isNormalQuickFilter && quickFilterRules && quickFilter?.rules?.length > 0 && (
                            <QuickFilterDrawer columns={settingColumns} quickFilter={quickFilter} schema={schema} onFilter={onFilter} />
                        )}

                        {enableSort && (
                            <Sort
                                columns={settingColumns}
                                userSortSetting={userSortSetting}
                                primaryField={firstTableProps}
                                data={sort}
                                isMobile={isMobile}
                                isShowDsName={dataSource.type === DataSourceType.joinDataSource}
                                onChangeSorter={onChangeSorter}
                            />
                        )}
                        {enableDisplay && (
                            <Display
                                dataSource={dataSource}
                                dataSourceList={dataSourceList}
                                columns={columns}
                                primaryField={firstTableProps || ''}
                                isMobile={isMobile}
                                disableIconColor
                                onChangeDisplay={onChangeDisplay}
                            />
                        )}
                        {enableGroup && (
                            <Group columns={settingColumns} schema={schema} isMobile={isMobile} data={group} onChange={onChangeGroup} />
                        )}
                        {enableSetKanban && viewType === 'kanban' && (
                            <KanbanGroup
                                columns={settingColumns}
                                schema={schema}
                                isMobile={isMobile}
                                kanbanGroupConfig={kanbanGroupConfig}
                                onKanbanGroupConfigChange={onKanbanGroupConfigChange}
                            />
                        )}
                        {/* 2024-04-29 暂时放开关联表可导入配置 */}
                        {enableImport && (
                            <Import
                                isMobile={isMobile}
                                viewColumns={columns}
                                appId={appId}
                                envId={envId}
                                dataSource={dataSource}
                                dataSourceList={dataSourceList}
                                userImportSetting={userImportSetting}
                                onAppend={onAppend}
                            />
                        )}
                        {action}
                    </SC.ViewSettingContent>
                </SC.ViewActionContainer>
                {enableSearch && isMobile && <DebounceSearch size="lg" isTheme isFull={isMobile} value={search} onSearch={onSearch} />}
            </SC.ViewSettingToolbarContent>
        )
    }, [
        action,
        appId,
        columns,
        dataSource,
        dataSourceList,
        enableDisplay,
        enableGroup,
        enableImport,
        enableSearch,
        enableSetKanban,
        enableSort,
        envId,
        firstTableProps,
        group,
        isMobile,
        isNormalQuickFilter,
        kanbanGroupConfig,
        mobileContentStyles,
        onAppend,
        onChangeDisplay,
        onChangeGroup,
        onChangeSorter,
        onFilter,
        onKanbanGroupConfigChange,
        onSearch,
        quickFilter,
        quickFilterRules,
        schema,
        search,
        settingColumns,
        showTitle,
        sort,
        title,
        userImportSetting,
        userSortSetting,
        viewType
    ])

    const isShowNoSettingBlockTitle = !isMobile && !isExistedSetting && showTitle && viewType !== 'custom'
    const isShowSettingBlockTitle = !isMobile && isExistedSetting && showTitle && viewType !== 'custom'
    const isShowFilterActor = quickFilterRules && quickFilterRules?.length > 0 && isTileQuickFilter

    return (
        <SC.ViewSettingToolbarContainer style={styles?.root}>
            {isShowSettingBlockTitle && <BlockTitle fontSize={20} title={title} />}
            {!noData && (
                <>
                    <SC.ViewSettingContainer style={{ flexDirection: isTileQuickFilter ? 'column' : 'row' }}>
                        {isShowFilterActor && (
                            <FilterActor
                                mode="tile"
                                dataSource={dataSource}
                                columns={settingColumns}
                                isMobile={isMobile}
                                quickFilter={quickFilter}
                                onFilter={onFilter}
                            />
                        )}
                        {isShowNoSettingBlockTitle && <BlockTitle fontSize={20} title={title} />}
                    </SC.ViewSettingContainer>
                    {settingContent}
                </>
            )}
        </SC.ViewSettingToolbarContainer>
    )
}
